import React from 'react';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { ListComponent, ListItemComponent } from './index.styles';
import { Typography } from '../../../typography';
import {
  InformationCardProps,
  InformationItem,
  ItemAction,
} from '../../InformationCard.types';
import IconButton from '@mui/material/IconButton';
import { ImageOrIcon } from '../ImageOrIcon';
import { Validate } from '@dewise-devex/backstage-plugin-utilities-common-client';

/**
 * Renders a list of items with optional images, actions, and text.
 *
 * The `CardList` component accepts data that contains items to display, as well as optional
 * configurations for background styling, actions, and custom components for empty states.
 *
 * @param props - The properties for the `CardList` component.
 *
 *
 * @example
 * ```tsx
 * const data = {
 *   items: [
 *     { primary: 'Item 1', secondary: 'Description 1', image: '/path/to/image.png' },
 *     { primary: 'Item 2', secondary: 'Description 2' },
 *   ],
 *   background: true,
 *   itemAction: [
 *     { icon: EditIcon, action: (item) => console.log(item) },
 *   ],
 * };
 *
 * <CardList data={data} asIcon={false} />
 * ```
 *
 * @category InformationCard
 * @document CardListComponentUsage.md
 */
export const CardList = (props: InformationCardProps): React.ReactElement => {
  if (props.data?.items) {
    if (Validate.isEmptyArray(props.data.items)) {
      if (props.data?.EmptyComponent) {
        return props.data.EmptyComponent;
      }
      return <Typography>No items to display</Typography>;
    }

    const isWithAction = !!props.data.itemAction;
    const isMultiAction = (props.data.itemAction?.length ?? 0) > 1;

    const onClick =
      !isMultiAction && props.data.itemAction
        ? (item: InformationItem) => props.data?.itemAction?.[0]?.action?.(item)
        : undefined;

    const asIcon = props?.asIcon === undefined ? true : props.asIcon;
    const imageSize = asIcon ? 18 : 40;

    return (
      <ListComponent>
        {props.data.items.map((item, index) => {
          return (
            <ListItemComponent
              alignItems="center"
              key={`${item.primary}-${index}_li_root`}
              onClick={onClick ? () => onClick(item) : undefined}
              hasBackground={props.data?.background}
            >
              {item.image && (
                <ListItemAvatar
                  sx={{
                    minWidth: asIcon ? 30 : 56,
                    marginLeft: asIcon ? '8px' : undefined,
                  }}
                >
                  <ImageOrIcon
                    key={`${item.primary}-${index}_li_avatar`}
                    alt={item.primary}
                    src={item.image as unknown as string}
                    sx={{
                      width: imageSize,
                      height: imageSize,
                      borderRadius:
                        item.metadata?.borderRadius ??
                        (!asIcon ? '50%' : 'unset'),
                    }}
                  />
                </ListItemAvatar>
              )}
              {renderText(item)}
              {isWithAction && renderItemAction(item, props.data?.itemAction)}
            </ListItemComponent>
          );
        })}
      </ListComponent>
    );
  } else {
    throw new Error('CardList component requires data prop to be passed');
  }
};

/**
 * Renders the primary and optional secondary text for a list item.
 *
 * @param item - The information item containing primary and secondary text.
 * @returns A `React.ReactElement` containing the text content.
 */
function renderText(item: InformationItem): React.ReactElement {
  if (!item.secondary) {
    return (
      <ListItemText>
        <Typography fontSize={14}>{item.primary}</Typography>
      </ListItemText>
    );
  }
  return (
    <ListItemText>
      <Typography fontSize={14}>{item.primary}</Typography>
      <Typography fontSize={12}>{item.secondary}</Typography>
    </ListItemText>
  );
}

/**
 * Renders action buttons for a list item.
 *
 * @param item - The information item for which the actions will be displayed.
 * @param actions - The list of actions, each containing an icon and a click handler.
 * @returns A `React.ReactElement` containing the action buttons, or `null` if no actions are provided.
 */
function renderItemAction(
  item: InformationItem,
  actions?: ItemAction[],
): React.ReactElement | null {
  if (!actions) {
    return null;
  }

  return (
    <>
      {actions.map((action, index) => {
        if (!action.icon) {
          return null;
        }
        return (
          <IconButton
            key={`${item.primary}-${index}`}
            style={{ fontSize: 18, padding: '6px' }}
            onClick={action.action ? () => action.action?.(item) : undefined}
          >
            <action.icon sx={{ width: 18, height: 18 }} fontSize="inherit" />
          </IconButton>
        );
      })}
    </>
  );
}
